/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.ui

import io.peekandpoke.klang.ui.HoverPopupCtrl
import io.peekandpoke.klang.ui.KlangUiToolContext
import io.peekandpoke.klang.ui.KlangUiToolEmbeddable
import io.peekandpoke.klang.ui.codetools.KlangToolAutoUpdate
import io.peekandpoke.klang.ui.feel.KlangTheme
import io.peekandpoke.klang.ui.svgCircle
import io.peekandpoke.klang.ui.svgRoot
import io.peekandpoke.kraft.components.Component
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.forms.formController
import io.peekandpoke.kraft.popups.PopupsManager.Companion.popups
import io.peekandpoke.kraft.semanticui.forms.UiInputField
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.html.css
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.semanticui.SemanticIconFn
import io.peekandpoke.ultra.semanticui.ui
import kotlinx.css.marginBottom
import kotlinx.css.minWidth
import kotlinx.css.px
import kotlinx.css.rem
import kotlinx.html.FlowContent
import kotlinx.html.Tag
import kotlinx.html.div
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

// ── Tool singleton ────────────────────────────────────────────────────────────

/** [KlangUiToolEmbeddable] for editing a euclidean rhythm string (pulses:steps:rotation). */
object SprudelEuclidEditorTool : KlangUiToolEmbeddable {
    override val title: String = "Euclid Editor"

    override val iconFn: SemanticIconFn = { bullseye }

    override fun FlowContent.render(ctx: KlangUiToolContext) {
        SprudelEuclidEditorComp(ctx, embedded = false)
    }

    override fun FlowContent.renderEmbedded(ctx: KlangUiToolContext) {
        SprudelEuclidEditorComp(ctx, embedded = true)
    }
}

// ── Entry-point helpers ───────────────────────────────────────────────────────

@Suppress("FunctionName")
private fun Tag.SprudelEuclidEditorComp(toolCtx: KlangUiToolContext, embedded: Boolean) =
    comp(SprudelEuclidEditorComp.Props(toolCtx, embedded)) { SprudelEuclidEditorComp(it) }

// ── Component ─────────────────────────────────────────────────────────────────

private class SprudelEuclidEditorComp(ctx: Ctx<Props>) : Component<SprudelEuclidEditorComp.Props>(ctx) {

    data class Props(val toolCtx: KlangUiToolContext, val embedded: Boolean = false)

    // ── Parse current value from raw source text ──────────────────────────────

    private val laf by subscribingTo(KlangTheme)
    private val autoUpdate by subscribingTo(KlangToolAutoUpdate)
    private val infoPopup = HoverPopupCtrl(popups)

    private val formCtrl = formController()

    private val initialValue = props.toolCtx.currentValue ?: ""

    private val parsed
        get() = run {
            val raw = parseStr(initialValue).orEmpty()
            val parts = raw.split(":").map { it.toIntOrNull() }
            Triple(
                parts.getOrNull(0) ?: 3,   // pulses
                parts.getOrNull(1) ?: 8,   // steps
                parts.getOrNull(2) ?: 0,   // rotation
            )
        }

    private var pulses by value(parsed.first)
    private var steps by value(parsed.second)
    private var rotation by value(parsed.third)

    private var resetCounter by value(0)

    // ── Helpers ───────────────────────────────────────────────────────────────

    private fun buildValue(): String = "\"$pulses:$steps:$rotation\""

    private val isInitialModified get() = initialValue != buildValue()
    private val isCurrentModified get() = (props.toolCtx.currentValue ?: "") != buildValue()

    private fun liveUpdate() {
        if (props.embedded || autoUpdate) {
            props.toolCtx.onCommit(buildValue())
        }
    }

    private fun onCancel() {
        if (!props.embedded && autoUpdate && isInitialModified) {
            props.toolCtx.onCommit(initialValue)
        }
        props.toolCtx.onCancel()
    }

    private fun onReset() {
        pulses = parsed.first
        steps = parsed.second
        rotation = parsed.third
        formCtrl.resetAllFields()
        props.toolCtx.onCommit(initialValue)
        resetCounter++
    }

    private fun onCommit() {
        props.toolCtx.onCommit(buildValue())
    }

    /** Compute euclidean rhythm pattern: returns list of booleans for each step. */
    private fun euclideanPattern(p: Int, s: Int, r: Int): List<Boolean> {
        if (s <= 0) return emptyList()
        val clampedP = p.coerceIn(0, s)
        val base = List(s) { i -> (i * clampedP % s) < clampedP }
        return if (r == 0) {
            base
        } else {
            val rot = r % s
            base.drop(rot) + base.take(rot)
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────

    override fun VDom.render() {
        if (props.embedded) {
            renderContent()
        } else {
            ui.segment {
                css { minWidth = 400.px }
                toolHeaderWithInfo("Euclidean Rhythm", props.toolCtx, infoPopup)
                renderContent()
                ui.divider {}
                ToolButtonBar(
                    isInitialModified = isInitialModified,
                    isCurrentModified = isCurrentModified,
                    onCancel = ::onCancel,
                    onReset = ::onReset,
                    onCommit = ::onCommit,
                )
            }
        }
    }

    private fun FlowContent.renderContent() {
        div {
            key = "euclid-editor-content-$resetCounter"

            ui.form {
                ui.three.stackable.fields {
                    UiInputField(pulses.toDouble(), { pulses = it.toInt().coerceAtLeast(0); liveUpdate() }) {
                        domKey("pulses")
                        step(1.0)
                        label("Pulses")
                    }
                    UiInputField(steps.toDouble(), { steps = it.toInt().coerceAtLeast(1); liveUpdate() }) {
                        domKey("steps")
                        step(1.0)
                        label("Steps")
                    }
                    UiInputField(rotation.toDouble(), { rotation = it.toInt().coerceAtLeast(0); liveUpdate() }) {
                        domKey("rotation")
                        step(1.0)
                        label("Rotation")
                    }
                }
            }
            ui.divider {}
            div {
                css { if (!props.embedded) marginBottom = 1.rem }
                renderEuclidSvg()
            }
        }
    }

    // ── SVG ───────────────────────────────────────────────────────────────────

    private fun FlowContent.renderEuclidSvg() {
        val w = 220.0
        val h = 220.0
        val cx = 110.0
        val cy = 110.0
        val circleR = 80.0
        val dotR = 7.0

        val pattern = euclideanPattern(pulses, steps, rotation)
        val safeSteps = steps.coerceAtLeast(1)

        svgRoot(viewBox = "0 0 $w $h") {
            svgCircle(cx, cy, circleR, fill = "none", stroke = "#e8e8e8", strokeWidth = "1")
            for (i in 0 until safeSteps) {
                // Start at top (12 o'clock) = -PI/2, go clockwise
                val angle = -PI / 2.0 + 2.0 * PI * i / safeSteps
                val dx = cx + circleR * cos(angle)
                val dy = cy + circleR * sin(angle)
                val active = pattern.getOrElse(i) { false }
                val fill = if (active) laf.gold else "#e8e8e8"
                val stroke = if (active) laf.gold else "#ccc"
                svgCircle(dx, dy, dotR, fill = fill, stroke = stroke, strokeWidth = "1.5")
            }
        }
    }
}
